import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Dialog, DialogTitle, IconButton, Stack } from '@mui/material';
import clsx from 'clsx';
import RatingChecklist from 'feature/document/DocumentContainer/DocumentChecklist/RatingChecklist';
import { Button } from 'global/components/Button';
import CommentContainer from 'global/components/CommentContainer';
import Form from 'global/components/form';
import { EmptySquareIcon } from 'global/components/PerformIcons';
import { PermissionControlledDisplay } from 'global/components/permission-gate';
import { Text } from 'global/components/text';
import TranslationManager from 'global/components/TranslationManager';
import { FILTERDEFAULTS } from 'global/utils/filter';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import { first, get } from 'global/lib/lodash';
import Styles, { useScreen } from 'global/styles';
import { cleanCopy } from 'global/utils';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { UploadFiles } from 'redux/actions/imageUploader';
import sendData from 'redux/actions/sendData';
import setFilter from 'redux/actions/setFilter';
import { IconContainer, LeftIcon, StyledDialogContent, TitleContainer } from './styles';

const CheckpointInfoModal = (props) => {
  const { open } = props;
  return !open ? null : <CheckpointInfoModalDetail {...props} />;
};

const CheckpointInfoModalDetail = (props) => {
  const { open, checkpoint, pssvCheckpoint, onClose, scorecard, checklist, edition, assessment } = props;
  const [optionVals, setOptionVals] = useState(props.optionVals || []);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialLanguageData, setInitialLanguageData] = useState({});
  const [modifiedLanguageData, setModifiedLanguageData] = useState({});
  const [formState, setFormState] = useState({});

  const classes = Styles();
  const { isMobile } = useScreen();
  const dispatch = useDispatch();
  const store = useStore();
  const savedPhotoFilters = useRef({});
  // Context: When coming from the scoring interface, we may be receiving checkpoint information from a processed-scorecard-score-value
  // Otherwise, we default to using checkpoint
  const inheritedCheckpointData = pssvCheckpoint || checkpoint;
  const checkpointId = get(inheritedCheckpointData, pssvCheckpoint ? 'checkpointId' : 'id');
  const editionId = get(scorecard, 'editionId');
  const ownerRealm = Number(get(scorecard, 'ownerRealmId', get(edition, 'ownerRealmId', null)));

  // Context: When coming from the Library, `edition` is defined and `scorecard` is undefined.
  // When coming from the Scoring page, `scorecard` is defined and `edition` is undefined.
  const config = scorecard?.config || edition?.config;

  const checkpointData = useRetrieveData(
    { url: `/admin/checkpoints/${checkpointId}` },
    {
      build: ['checkpoints'],
      params: {
        editMode: 1,
        editionId: editionId,
        include: 'images,checkpointOptions',
      },
      transformData: (d) => {
        const item = first(d);
        if (!item) return item;
        const translations = { ...get(item, 'description', {}) };
        delete translations.en;
        // Return a new object — avoid mutating the cached API data and avoid side effects
        return { ...item, description: item.description?.en, _translations: translations };
      },
    }
  );

  useEffect(() => {
    const item = checkpointData.data;
    if (!item) return;
    const translations = item._translations || {};
    setInitialLanguageData(translations);
    setModifiedLanguageData(translations);
    setFormState({ ...item });
  }, [checkpointData.data]);

  const { userBuilderRealms: builders } = useUser();

  // When coming from the Library and using a rating checklist, select the single checkpoint whose `checkpointId` matches an entry in `checklist.checkpoints`.
  // When coming from the Scoring page, `checkpointData` is already the selected checkpoint.
  const selectedCheckpoint = edition ? get(checklist, 'checkpoints', []).find((c) => c.id === checkpointId) : get(checkpointData, 'data');

  const editVals = get(checkpointData, 'data.editMode', []);
  const canEditCheckpoint = get(editVals, 'owned', false);
  // Users assigned multiple organizations can edit comments, despite not editing the checkpoint directly
  const canEditComments = canEditCheckpoint || builders.some((b) => Number(b.id) === ownerRealm);

  const longTitle = get(checkpointData, 'data.name', '').length > 2;

  // If we're viewing this from the scoring interface, we need to build the optionVals from the scorecard min and max scores.
  useEffect(() => {
    if (scorecard && optionVals.length === 0) {
      const min = parseInt(get(scorecard, 'config.minScore', 0));
      const max = parseInt(get(scorecard, 'config.maxScore', 1));
      const step = parseInt(get(scorecard, 'config.step', 1)) || 1;

      const vals = [];
      const range = max - min;
      for (let i = min; i <= max; i += step) {
        vals.push(range > 0 ? Math.round(((i - min) / range) * 100) : 0);
      }
      setOptionVals(vals);
    }
  }, [scorecard]);

  const isRating = optionVals.length > 0 && !(get(config, 'minScore') === 0 && get(config, 'maxScore') === 1);

  const handleFormSubmit = async () => {
    setIsEditMode(false);

    const notes = get(formState, 'notes', '');
    const images = get(formState, 'images') || [];
    const deletedLanguages = Object.keys(initialLanguageData).filter((lang) => !(lang in modifiedLanguageData));
    const [firstLanguage, ...otherLanguages] = Object.keys(modifiedLanguageData);

    const buildCheckpointInfoData = (attributes) => ({
      data: {
        id: checkpointId.toString(),
        type: 'checkpoints',
        attributes,
      },
    });

    for (const languageCode of deletedLanguages) {
      await sendData({
        url: `/admin/checkpoints/${checkpointId}`,
        method: 'patch',
        data: buildCheckpointInfoData({ description: null, edition_id: editionId, lang: languageCode }),
      });
    }

    // First (or only) request always carries notes + images; optionally includes first language
    const firstPayload = buildCheckpointInfoData({
      notes,
      images,
      edition_id: editionId,
      ...(firstLanguage && { description: modifiedLanguageData[firstLanguage], lang: firstLanguage }),
    });
    await UploadFiles(firstPayload);
    await sendData({ url: `/admin/checkpoints/${checkpointId}`, method: 'patch', data: firstPayload });

    for (const languageCode of otherLanguages) {
      await sendData({
        url: `/admin/checkpoints/${checkpointId}`,
        method: 'patch',
        data: buildCheckpointInfoData({ description: modifiedLanguageData[languageCode], edition_id: editionId, lang: languageCode }),
      });
    }

    // Signal the parent that the checkpoint changed so its onClose triggers a refetch;
    // without this the saved images/notes don't appear in the list until a hard refresh.
    props.setNeedsUpdate?.(true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            width: "100%",
            alignItems: "center"
          }}>
          <LeftIcon>
            <IconButton onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
          </LeftIcon>

          <TitleContainer>
            <Text type="label_large">Checkpoint Info</Text>
          </TitleContainer>
        </Stack>
      </DialogTitle>
      <StyledDialogContent>
        {!formState?.id ? null : <Form
          key={`${isEditMode ? 'edit' : 'view'}-${formState.id}`}
          showAllScreens={false}
          showNavigation={false}
          loading={checkpointData.loading}
          onFormState={(fs) => setFormState((prev) => ({ ...prev, ...fs }))}
          loadingMessage={'Please Wait...'}
          screens={[
            {
              title: '',
              inputs: [
                {
                  id: 'checkpointTitle',
                  component: () => (
                    <div>
                      <Text type="title_large">
                        <strong>
                          {get(inheritedCheckpointData, 'checklist.name', get(checklist, 'name', ''))} : {get(checkpointData, 'data.name', '')}
                        </strong>
                      </Text>
                    </div>
                  ),
                },
                {
                  id: 'checkpointDescription',
                  component: () => (
                    <>
                      {isRating ? (
                        <div>
                          <Text type="label_medium">Rating</Text>
                          <Text type="body_medium">{get(checkpointData, 'data.rating')}</Text>
                          <RatingChecklist
                            {...props}
                            editMode={false}
                            checklist={{ ...checklist, checkpoints: [selectedCheckpoint] }}
                            edition={{ ...edition, config: config }}
                            optionVals={optionVals}
                          />
                        </div>
                      ) : (
                        <div>
                          <div className={clsx(classes.row, classes.hlRow)}>
                            <LeftIcon>
                              <IconContainer>
                                <EmptySquareIcon />
                              </IconContainer>
                            </LeftIcon>
                            <div className={classes.rowText}>
                              <strong className={!longTitle && classes.capitalize}>
                                {get(checkpointData, 'data.name', '')}
                                {!longTitle && `.`}
                                {` `}
                              </strong>
                              <span>{get(checkpointData, 'data.description', '')}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ),
                },

                {
                  id: 'translations',
                  props: {
                    label: 'Translations',
                  },
                  component: () => <TranslationManager isEditMode={isEditMode} modifiedLanguageData={modifiedLanguageData} setModifiedLanguageData={setModifiedLanguageData} />,
                },
                {
                  id: 'notes',
                  type: 'TextField',
                  defaultValue: () => get(formState, 'notes'),
                  props: {
                    label: 'Notes',
                    multiline: true,
                    placeholder: '',
                    disabled: !isEditMode,
                  },
                },
                {
                  id: 'images',
                  type: 'DropArea',
                  defaultValue: () => get(formState, 'images'),
                  props: {
                    label: 'Images',
                    notAllowed: ['tag'],
                    uploadAllNew: true,
                    collection: 'checkpoint-images',
                    shouldUpdateOnCaptionChange: false,
                    disabled: !isEditMode,
                    spaceBetween: 3,
                    assessment,
                    ...(isEditMode
                      ? get(assessment, 'data')
                        ? {
                            imageSelect: {
                              assessment: get(assessment, 'data'),
                              superset_id: get(pssvCheckpoint, 'supersetId', get(checklist, 'supersetId')),
                            },
                          }
                        : {
                            imageSelect: {
                              useFullPhotos: true,
                              display: 'photo',
                              onOpen: () => {
                                savedPhotoFilters.current = cleanCopy(get(store.getState(), 'filter.reports.photo', {}));
                                dispatch(
                                  setFilter('reports', 'photo', {
                                    ...get(FILTERDEFAULTS, 'reports.photo', {}),
                                    searchstring: get(checklist, 'name'),
                                    superset_id: get(checklist, 'classification.system.id', get(checklist, 'supersetId')),
                                  })
                                );
                              },
                              onClose: () => {
                                dispatch(setFilter('reports', 'photo', savedPhotoFilters.current));
                              },
                            },
                          }
                      : {}),
                  },
                },
                {
                  id: 'editModePermissionGate',
                  component: () => {
                    return canEditCheckpoint ? (
                      <CheckpointInfoEditPermission
                        isEditMode={isEditMode}
                        onCancel={() => {
                          setIsEditMode(false);
                          setFormState({ ...checkpointData.data });
                          setModifiedLanguageData({ ...initialLanguageData });
                        }}
                        onSave={handleFormSubmit}
                        onEdit={() => setIsEditMode(true)}
                        allowedPermissions={['manageAccount', 'createLibrary']}
                      />
                    ) : null;
                  },
                },
                {
                  id: 'comments',
                  component: () => {
                    return canEditComments ? <CheckpointInfoComments {...props} allowedPermissions={['manageAccount', 'createLibrary']} data={checkpointData.data} /> : null;
                  },
                },
              ],
            },
          ]}
        />}
      </StyledDialogContent>
    </Dialog>
  );
};

const CheckpointInfoEditPermission = PermissionControlledDisplay((props) => {
  const { isEditMode, onCancel, onSave, onEdit } = props;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {isEditMode ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Button variant="text" startIcon={<CloseIcon />} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave}>
            Save
          </Button>
        </div>
      ) : (
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
          Edit
        </Button>
      )}
    </div>
  );
});

const CheckpointInfoComments = PermissionControlledDisplay((props) => {
  const checkpointId = props.pssvCheckpoint?.checkpointId || props.checkpoint?.id;
  const [showCreateComment, setShowCreateComment] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  const uhook = useUser();
  const { me } = uhook;

  const [commentState, setCommentState] = useState({
    type: 'comments',
    attributes: {
      user_id: get(me, 'id'),
      commentable_id: checkpointId,
      commentable_type: 'checkpoint',
      body: '',
      images: [],
      meta: {},
    },
  });
  const commentsData = useRetrieveData(
    { url: `/comments` },
    {
      build: ['comments'],
      params: {
        filter: {
          commentable_id: checkpointId,
          commentable_type: 'checkpoint',
        },
        include: 'images,author',
      },
    }
  );

  // the comment argument here comes from CommentContainer when it calls onSubmitComment
  const handleSubmitComment = async (comment) => {
    try {
      setCommentLoading(true);
      const submitData = { data: comment };

      await UploadFiles(submitData, { attributeType: 'images' });
      await sendData({
        url: '/comments',
        method: 'post',
        data: submitData,
      });
      commentsData.update({ force: true });
      // PFM-2106: a new checkpoint comment changes what the scoring Info button's red badge
      // reflects; flag the parent so it refetches on close (notes/images path does the same
      // via handleFormSubmit). Safe no-op when opened outside the scoring interface.
      props.setNeedsUpdate?.(true);
      setShowCreateComment(false);
      setCommentState((prev) => ({ ...prev, attributes: { ...prev.attributes, body: '', images: [] } }));
    } catch (err) {
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <CommentContainer
      onSubmitComment={handleSubmitComment}
      data={props.checkpoint}
      commentableId={checkpointId}
      commentableType={'checkpoint'}
      commentState={commentState}
      comments={commentsData}
      setCommentState={setCommentState}
      showCreateComment={showCreateComment}
      onShowCreateComment={setShowCreateComment}
      loading={commentLoading}
      helperText={
        <Text variant="display-small">
          Checkpoint comments are only visible to <strong>content admins</strong>. The intended use is for suggesting modifications or having other administrative dialogue.
        </Text>
      }
      labelText="Comments"
    />
  );
});

export default CheckpointInfoModal;
